import type { OnboardingV2Variant } from '@/features/flags/legacy-flags';
import type { AuthType, ProviderValue } from '@/models/analytics/features/user';
import type { DictateAnalyticsStep } from '@heidi/dictate';
import type { PersonalizationStep } from '@heidi/growth/contracts';
import { type OrganisationRole, type OrganisationSize } from '@heidi/schema/ml-scribe';

/**
 * Which arm of the guided-tour experiment the user is in. Only treatment users
 * on a preview-capable viewport are eligible, so this is absent — not
 * `sample_conversation` — for everyone else. The tour is now its own
 * `onboarding_variant` (`experimental-3`); this stays for continuity of the
 * existing funnels, which were built on it.
 */
export type OnboardingSubVariant = 'guided_tour' | 'sample_conversation';

/** The Heidi II agents tour for existing users. Not an arm of the v2 experiment. */
export type Heidi2OnboardingVariant = 'heidi_2_agents';

/** Steps of the Heidi II agents tour, plus leaving it from the header. */
export type Heidi2OnboardingAnalyticsStep =
  | 'welcome'
  | 'assign_tasks'
  | 'tell_heidi'
  | 'heidi_works_1'
  | 'heidi_works_2'
  | 'final_approval'
  | 'skip_tour';

/**
 * Onboarding flow: `control` (legacy form) | `experimental-2` (sample
 * conversation) | `experimental-3` (guided tour), or the Heidi II agents tour.
 */
export type OnboardingVariantProp = {
  onboarding_variant?: OnboardingV2Variant | Heidi2OnboardingVariant;
  onboarding_sub_variant?: OnboardingSubVariant;
};

export enum AuthenticationProvider {
  EMAIL = 'email',
  GOOGLE = 'google',
  MICROSOFT = 'microsoft',
  APPLE = 'apple',
}

export enum AuthenticationFlow {
  SIGN_UP = 'sign_up',
  LOGIN = 'login',
}

// Properties always in this case: onboarding_step
export enum OnboardingStep {
  PICK_ONBOARDING_FLOW = 'pick_onboarding_flow',
  SIGN_UP_FORM = 'sign_up_form',
  CUSTOMIZE_ORGANIZATION = 'customize_organization',
}

export enum OnboardingFlow {
  SELF_SERVICE = 'self_service',
  BOOK_DEMO = 'book_demo',
}

export type OnboardingStepCompletedEventProps = OnboardingVariantProp & {
  step:
    | OnboardingStep
    | PersonalizationStep
    | DictateAnalyticsStep
    | Heidi2OnboardingAnalyticsStep;
  source?: string;
  flow?: OnboardingFlow;
  organisation_size?: OrganisationSize;
  organisation_role?: OrganisationRole;
  ehr_provider?: string;
  authentication_provider?: AuthenticationProvider;
  step_number?: number;
  duration_ms?: number;
  /** Dictate onboarding — permissions step. */
  mic_granted?: boolean;
  accessibility_granted?: boolean;
  /** Dictate onboarding — key-confirm step. */
  hotkey_confirmed?: boolean;
  /** Dictate onboarding — key-selection step. */
  hotkey_custom?: boolean;
  /** Dictate onboarding — sign-in step. */
  auth_method?: string;
  /** Dictate onboarding — languages step. */
  languages_count?: number;
  /**
   * True when the phone number entered on a signup step (role / about-you /
   * sign-up form) passed validation at the moment the step was completed.
   * Supports the `release-phone-number-in-signup-*` experiment — see
   * {@link SignupPhoneFieldViewedEventProps}.
   */
  phone_number_valid?: boolean;
};

export type OnboardingStartedEventProps = OnboardingVariantProp & {
  source?: string;
  region?: string;
  /** Dictate onboarding — true when flow resumed from disk past permissions. */
  resumed_from_disk?: boolean;
};

export type OnboardingCompletedEventProps = OnboardingVariantProp & {
  source?: string;
  total_duration_ms?: number;
  steps_completed?: number;
};

export type OnboardingStepSkippedEventProps = OnboardingVariantProp & {
  step_name: PersonalizationStep | DictateAnalyticsStep | Heidi2OnboardingAnalyticsStep;
  source?: string;
  /**
   * GRO-3157: preserves the `tasks_completed_count` dimension that
   * `onboarding_learn_basics_skipped` used to carry, so the note-edit skip
   * funnel can still segment by depth-of-engagement after we migrated to
   * the unified `onboarding_step_skipped` event. Optional — only set by the
   * note-edit skip handler today.
   */
  tasks_completed_count?: number;
};

export type OnboardingTemplateStartedEventProps = OnboardingVariantProp & {
  used_suggestion: boolean;
  specialty?: string;
};

export type OnboardingTemplateCompletedEventProps = OnboardingVariantProp & {
  duration_ms: number;
};

export type OnboardingTemplateReviewedEventProps = OnboardingVariantProp & {
  made_changes: boolean;
};

export type OnboardingDocumentsSelectedEventProps = OnboardingVariantProp & {
  count: number;
  doc_types: string[];
};

export type OnboardingAuthenticationCompletedEventProps = OnboardingVariantProp & {
  authentication_flow: AuthenticationFlow;
  provider?: ProviderValue;
  auth_type?: AuthType;
  custom_utm_source?: string | null;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  ttclid?: string;
  gbraid?: string;
  wbraid?: string;
  rdt_cid?: string;
  /** Feature that attributed/drove the user to sign up (e.g., 'evidence') */
  last_attributed_feature?: string | null;
};

export type OrganisationSurveySubmittedEventProps = {
  organisation_name: string;
  organisation_size?: OrganisationSize;
  organisation_role?: OrganisationRole;
  specialty_id?: string;
  specialty_name?: string;
  other_specialty?: string;
  ehr_provider?: string;
  is_official_practice?: boolean;
  cqc_practice_id?: string;
};

export type OnboardingNoteStyleUpdatedEventProps = OnboardingVariantProp & {
  abbreviations_enabled: boolean;
  duration_ms: number;
};

// PII-safe: raw query text is intentionally not sent.
export type OnboardingEvidenceQueriedEventProps = OnboardingVariantProp & {
  query_type: 'suggested' | 'custom';
  query_length: number;
};

export type OnboardingEvidenceAcceptedEventProps = OnboardingVariantProp & {
  items_count: number;
};

export type OnboardingEvidenceDiscardedEventProps = OnboardingVariantProp & {
  items_count: number;
};

export type OnboardingDocumentCreatedEventProps = OnboardingVariantProp & {
  document_type: string;
};

export type OnboardingLearnBasicsCompletedEventProps = OnboardingVariantProp & {
  tasks_completed: string[];
  duration_ms: number;
};

export type OnboardingLearnBasicsSkippedEventProps = OnboardingVariantProp & {
  tasks_completed_count: number;
};

// onboarding_updated event
export type OnboardingUpdatedEventProps = {
  step?: string;
};

/** Which signup surface rendered the phone-number field. */
export type SignupPhoneFieldSurface =
  | 'onboarding_role_step'
  | 'onboarding_about_you_step'
  | 'signup_modal'
  | 'organisation_survey';

/**
 * Fired when the signup phone-number field is shown, capturing whether the
 * user sees it as optional or mandatory (required). Drives analysis of the
 * `release-phone-number-in-signup-*` (and `country-config`) experiment.
 */
export type SignupPhoneFieldViewedEventProps = {
  phone_field_mode: 'optional' | 'required';
  country_code?: string;
  surface: SignupPhoneFieldSurface;
  onboarding_variant?: OnboardingV2Variant;
  /** ISO alpha-2 country code whose `country-config` entry was allotted (or null).
   *  `phone_field_mode` above already conveys the resolved phone variant. */
  country_config?: string | null;
};
